// #region Imports
import { useEffect, useMemo } from "react";
import { motion, useAnimate, stagger } from "framer-motion";
// #endregion


const variants = {
	0: "col-span-2 row-span-8 small:col-span-5 small:row-span-4",
	1: "col-span-3 row-span-3 col-start-3 small:col-span-4 small:row-span-2",
	2: "col-span-2 row-span-5 col-start-3 small:col-span-4 small:row-span-2 small:col-start-6",
	3: "col-span-6 row-span-3 col-start-5 small:col-span-9 small:row-span-2",
	4: "col-span-5 row-span-5 col-start-6 row-start-1 small:col-span-9 small:row-span-2",
};


const variantsBg = {
	0: [
		"linear-gradient(180deg, #A47FC2 0%, #5B87AF 20%, #008D6B 100%)",
		"linear-gradient(180deg, #008D6B 0%, #5B87AF 50%, #293146 100%)",
		"linear-gradient(180deg, #008D6B 0%, #5B87AF 50%, #293146 100%)",
		"linear-gradient(180deg, #A47FC2 0%, #5B87AF 20%, #008D6B 100%)",
	],

	1: [
		"linear-gradient(180deg, #CC9600 0%, #7772B5 20%, #C477AA 100%)",
		"linear-gradient(180deg, #C477AA 0%, #C477AA 50%, #CC9600 100%)",
		"linear-gradient(180deg, #C477AA 0%, #C477AA 50%, #CC9600 100%)",
		"linear-gradient(180deg, #CC9600 0%, #7772B5 20%, #C477AA 100%)",
	],

	2: [
		"linear-gradient(180deg, #511867 0%, #792872 20%, #CE0404 100%)",
		"linear-gradient(180deg, #CE0404 0%, #511867 50%, #792872 100%)",
		"linear-gradient(180deg, #CE0404 0%, #511867 50%, #792872 100%)",
		"linear-gradient(180deg, #511867 0%, #792872 20%, #CE0404 100%)",
	],

	4: [
		"linear-gradient(180deg, #DC67A1 0%, #6719AF 20%, #BE3182 100%)",
		"linear-gradient(180deg, #BA77C1 0%, #6719AF 40%, #DC67A1 80%)",
		"linear-gradient(180deg, #BA77C1 0%, #6719AF 40%, #DC67A1 89%)",
		"linear-gradient(180deg, #DC67A1 0%, #6719AF 20%, #BE3182 100%)",
	],

	3: [
		"linear-gradient(180deg, #254FA8 0%, #A883F0 20%, #AC73D0 100%)",
		"linear-gradient(180deg, #254FA8 10%, #A883F0 55%, #A883F0 100%)",
		"linear-gradient(180deg, #254FA8 10%, #A883F0 55%, #AC73D0 100%)",
		"linear-gradient(180deg, #254FA8 0%, #A883F0 20%, #AC73D0 100%)",
	],
};


function DashboardButton({
	index,
	pillars,
	opacity,
	title,
	pillarOpacity,
	borderColor,
	onOpen,
}) {

	const [scope, animate] = useAnimate();

	const backgrounds = variantsBg[index] ?? variantsBg[0];

	const pillarArray = useMemo(
		() => Array.from({ length: pillars ?? 4 }),
		[pillars]
	);


	// #region Pillar Animation

	useEffect(() => {
		animate(
			".left",
			{
				background: backgrounds,
			},
			{
				repeat: Infinity,
				ease: "easeInOut",
				duration: 10,
				delay: stagger((index * 0.1) + 0.2, {
					startDelay: 0.2,
				}),
			},
		);
	}, [animate, backgrounds, index]);

	// #endregion


	return (
		<motion.button
			onClick={onOpen}
			className={`
				rounded-3xl flex overflow-hidden
				${variants[index]} relative items-center
				transition-all ease-in-out duration-300
				text-ivory cursor-pointer
				w-full h-full border
			`}
			style={{
				opacity,
				borderColor,
			}}
		>

			{/* Animated pillars */}

			<motion.div
				style={{ opacity: pillarOpacity }}
				className="
					w-full flex flex-col justify-start
					items-center h-full select-none
					overflow-clip absolute z-0
				"
			>

				<div
					className="
						w-full h-full absolute top-0
						z-10 object-cover bg-noise
					"
					fetchPriority="high"
				/>

				<ul
					className="w-full flex h-full"
					ref={scope}
				>
					{pillarArray.map((_, pillarIndex) => (
						<li
							key={pillarIndex}
							className="
								left pillar-left h-full
								w-[30%] -ml-1
							"
						/>
					))}
				</ul>

			</motion.div>


			{/* Dashboard Content */}

			<motion.div
				className="
					relative z-10 text-left w-full h-full
					p-4 hover:p-6 transition-all
					ease-in-out duration-300
					flex flex-col items-end justify-between
					rounded-3xl small:rounded-xl small:p-4
					small:hover:p-4
				"
			>

				<img
					alt=""
					loading="lazy"
					src="/icons/arrowWhite.svg"
                    className="small:h-6 small:w-6"
				/>

				<h3 className="text-[3vw] self-start small:text-xl">
					{title.toUpperCase()}
				</h3>

			</motion.div>

		</motion.button>
	);
}


export default DashboardButton;